<template>
  <div class="app-emails-control">
    <div
      class="app-emails-control__main"
      :class="{
        'app-emails-control__main--cursor-text':
          !innerEmails.length && !isPlaceholder,
      }"
      @click="setInputFocus"
      v-click-outside="togglePlaceholder"
    >
      <app-title-box
        v-if="title"
        class="app-emails-control__title"
        size="small"
        :title="title"
        :isTooltip="!!tooltip"
        :tooltip="tooltip"
        :isRequired="isRequired"
      ></app-title-box>
      <div v-if="isPlaceholder" class="app-emails-control__placeholder">
        {{ placeholder }}
      </div>
      <div v-else class="app-emails-control__content">
        <template v-for="(field, index) in fields">
          <input
            v-if="field.isInput"
            ref="input"
            :key="field.id"
            class="app-emails-control__input"
            v-autowidth="{
              maxWidth: 'none',
              minWidth: '10px',
              comfortZone: 1,
            }"
            :class="{
              'app-emails-control__input--invalid': errorMsg,
              'app-emails-control__input--first': fields.length < 2,
            }"
            @keyup.enter="handlerKeyup(field.isEdit, index)"
            @keyup.space="handlerKeyup(field.isEdit, index)"
            :value="value"
            @keydown="handlerKeydown"
            @input="handlerInput(index, $event)"
            v-click-outside="onClickOutside"
          />
          <!-- @input="innerIndexField = index" -->

          <app-tag
            v-else
            :key="field.id"
            class="app-emails-control__email"
            :title="field.value"
            isCloseBtn
            @remove="removeFromList(index)"
            @click="handlerTagClick(field.id, index)"
          ></app-tag>
        </template>
      </div>
    </div>
    <small v-if="errorMsg" class="app-input-group__invalid">
      {{ errorMsg }}
    </small>
  </div>
</template>

<script>
import * as REGEX from '@/helpers/enums/regex';
import { nanoid } from 'nanoid';

export default {
  name: 'AppEmailsControl',
  components: {
    AppTitleBox: () => import('@/components/common/AppTitleBox'),
    AppTag: () => import('@/components/ui/AppTag'),
  },
  model: {
    prop: 'emails',
    event: 'change',
  },
  props: {
    isReadonly: {
      type: Boolean,
    },
    isDisabled: {
      type: Boolean,
    },
    isRequired: {
      type: Boolean,
    },
    tooltip: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    emails: {
      type: Array,
      require: true,
    },
    id: {
      type: Number,
      default: 0,
    },
    placeholder: {
      type: String,
      default: 'example@gmail.com',
    },
    limit: {
      type: Number,
      default: 5,
    },
  },
  data: (vm) => ({
    innerEmails: vm.emails,
    fields: [{ id: nanoid(), value: '', isInput: true, isEdit: false }],
    value: '',
    oldValue: '',
    errorMsg: '',
    innerIndexField: null,
    isPlaceholder: true,
    isInputFieldProcess: false,
  }),
  computed: {
    /**
     * Валидация email
     */
    isEmailValid() {
      return this.value.length ? REGEX.EMAIL.test(this.value) : true;
    },
    /**
     * Проверка email на уникальность
     */
    isEmailUnique() {
      if (this.oldValue === this.value) {
        return true;
      }
      return !this.emails.includes(this.value);
    },
  },
  watch: {
    isPlaceholder() {
      this.value = '';
    },
    value(value) {
      if (!value.length && this.isRequired && !this.innerEmails.length) {
        this.errorMsg = 'Укажите email';
      } else if (!value.length) {
        this.errorMsg = '';
      }
    },
    errorMsg(value) {
      this.$emit('error', !!value);
    },
  },
  created() {
    const emails = this.innerEmails.map((el) => this.newEmail(el));
    this.fields = emails.concat([this.defaultInput()]);
    this.isPlaceholder = this.fields.length < 2;
  },
  methods: {
    /**
     * Событие input
     * @param idx
     * @param event
     */
    handlerInput(idx, event) {
      this.value = event.target.value;
      this.innerIndexField = idx;
    },
    /**
     * Событие keydown
     * @param event
     */
    handlerKeydown(event) {
      this.value = event.target.value;
    },
    /**
     * Значения дефолтного инпута
     */
    defaultInput() {
      return {
        id: nanoid(),
        value: '',
        isInput: true,
        isEdit: false,
      };
    },
    /**
     * Объект нового имейла
     */
    newEmail(value) {
      return {
        id: nanoid(),
        value,
        isInput: false,
        isEdit: true,
      };
    },
    /**
     * Переключатель отображния плейсхолдера
     */
    togglePlaceholder() {
      if (this.fields.length > 1 || this.value) {
        this.isPlaceholder = false;
      } else {
        this.isPlaceholder = true;
      }
    },
    /**
     * Валидация поля email (может вызываться из вне)
     */
    validateEmail() {
      this.errorMsg = '';
      if (!this.value.length && this.isRequired && !this.innerEmails.length) {
        this.errorMsg = 'Укажите email';
        return false;
      }
      if (this.innerEmails.length > this.limit) {
        this.errorMsg = 'Достигнут максимум почтовых адресов';
        return false;
      }
      if (!this.isEmailValid) {
        this.errorMsg = 'Введен некорректный email';
        return false;
      }
      if (!this.isEmailUnique) {
        this.errorMsg = 'Такой email уже существует';
        return false;
      }
      return true;
    },
    /**
     * Событие keyup
     * @param {Boolean} isEdit - редактирвание существующего имейла
     * @param {Number} idx - индекс
     */
    handlerKeyup(isEdit, idx) {
      if (isEdit) {
        this.editEmail(idx);
      } else {
        this.addEmail();
      }
    },
    /**
     * Добавление нового email
     */
    addEmail() {
      this.value.trim();
      if (!this.value.length) {
        this.errorMsg = '';
        this.oldValue = '';
      } else if (this.validateEmail()) {
        this.innerEmails.push(this.value);
        this.fields.splice(-1, 0, this.newEmail(this.value));
        this.value = '';
        if (this.innerEmails.length >= this.limit) {
          this.removeLastElement();
        } else {
          this.setInputFocus();
        }
        this.$emit('change', this.innerEmails);
        this.innerIndexField = null;
      }
    },
    /**
     * Редактирование имейла
     * @param {Number} idx - индекс
     */
    editEmail(idx) {
      this.value.trim();
      if (!this.value.length) {
        this.errorMsg = '';
        this.oldValue = '';
      } else if (this.validateEmail()) {
        this.innerEmails.splice(idx, 1, this.value);
        this.fields.splice(idx, 1, this.newEmail(this.value));
        if (this.innerEmails.length < this.limit) {
          this.fields.push(this.defaultInput());
          this.setInputFocus();
        }
        this.removeValues();
        this.$emit('change', this.innerEmails);
      }
    },
    /**
     * Установить инпут в фокусе
     */
    setInputFocus() {
      if (this.innerEmails.length >= this.limit) {
        return false;
      }
      this.isPlaceholder = false;
      this.$nextTick(() => {
        this.$refs.input[0].focus();
      });
      return true;
    },
    /**
     * Удаление email
     * @param {Number} idx - индекс
     */
    removeFromList(idx) {
      if (this.errorMsg) {
        this.fields.forEach((el, index) => {
          if (index === this.innerIndexField) {
            // eslint-disable-next-line no-param-reassign
            el.value = this.oldValue;
            // eslint-disable-next-line no-param-reassign
            el.isInput = false;
          }
        });
        this.removeValues();
      }
      this.innerEmails.splice(idx, 1);
      this.fields.splice(idx, 1);
      if (this.fields[this.fields.length - 1].isEdit) {
        this.fields.push(this.defaultInput());
      }

      this.setInputFocus();
    },
    /**
     * Удаление последнего элемента
     */
    removeLastElement() {
      this.fields.splice(-1, 1);
    },
    /**
     * Очистка значений
     */
    removeValues() {
      this.value = '';
      this.oldValue = '';
      this.innerIndexField = null;
    },
    /**
     * Клик по полю
     * @param {String} id - id поля
     * @param {Number} idx - индекс
     */
    handlerTagClick(id, idx) {
      this.setInputField(id, idx);
    },
    /**
     * Заменяет тег на инпут
     * @param {String} id - id поля
     * @param {Number} idx - индекс
     */
    setInputField(id, idx) {
      this.isInputFieldProcess = true;
      if (!this.validateEmail() && this.innerIndexField !== null) {
        return false;
      }
      this.innerIndexField = idx;
      if (this.fields.some((el) => !el.isEdit)) {
        this.removeLastElement();
      }
      this.fields.forEach((el) => {
        if (el.id === id) {
          // eslint-disable-next-line no-param-reassign
          el.isInput = true;
          this.oldValue = el.value;
          this.value = el.value;
        } else {
          // eslint-disable-next-line no-param-reassign
          el.isInput = false;
        }
      });
      this.setInputFocus();
      this.isInputFieldProcess = false;
      return false;
    },
    /**
     * Клик вне инпута
     */
    onClickOutside() {
      if (this.isInputFieldProcess) {
        return false;
      }
      if (!this.validateEmail()) {
        return false;
      }
      if (this.innerIndexField !== null) {
        this.editEmail(this.innerIndexField);
        return false;
      }
      this.errorMsg = '';
      this.removeValues();
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
.app-emails-control {
  $root: &;

  position: relative;
  width: 100%;

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 7px 12px;
    padding-bottom: 0;
    cursor: pointer;
    background: $white;
    border: 1px solid $gray-light;
    border-radius: 2px;
    transition: border $A;

    &:hover {
      border-color: $color-brand-green;
    }

    &--focused {
      border-color: $color-brand-green;
    }

    &--cursor-text {
      cursor: text;
    }
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    min-height: 32px;
    margin-top: 5px;
  }

  &__placeholder {
    margin-top: 8px;
    margin-bottom: 5px;
    font-size: rem(14);
    font-weight: 250;
    line-height: 24px;
    color: $black;
    letter-spacing: 0.02em;
  }

  &__input {
    align-self: center;
    height: 22px;
    padding: 0;
    margin-right: 8px;
    margin-bottom: 8px;
    font-size: rem(14);
    font-weight: 250;
    line-height: 22px;
    color: $black;
    letter-spacing: 0.02em;
    border: none;

    &--invalid {
      color: #ee3224;
    }

    &--first {
      margin-top: 5px;
      margin-bottom: 5px;
      line-height: 24px;
    }
  }
}
</style>
